import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { isPast } from 'date-fns'

import { Menu } from '@percent/lemonade'
import {
  GivingCampaignStatusDto,
  type GivingCampaignPartnerDto
} from '@percent/workplace-giving/api/campaigns/campaign.types'
import { useGivingCampaignActions } from './useGivingCampaignActions'

export type ActionsMenuProps = Readonly<{
  campaign: GivingCampaignPartnerDto
}>

export function ActionsMenu({ campaign }: ActionsMenuProps) {
  const { t } = useTranslation()

  const items = useMemo(() => {
    if (campaign.status === GivingCampaignStatusDto.DRAFT && campaign.endDate && isPast(new Date(campaign.endDate))) {
      return [
        {
          key: 'view',
          label: t('workplace_giving.common.view')
        },
        {
          key: 'edit',
          label: t('workplace_giving.common.edit')
        },
        {
          key: 'cancel',
          label: t('workplace_giving.myCampaigns.actions.cancelConfirmationModal.title')
        }
      ]
    }

    if (campaign.status === GivingCampaignStatusDto.DRAFT) {
      return [
        {
          key: 'view',
          label: t('workplace_giving.common.view')
        },
        {
          key: 'edit',
          label: t('workplace_giving.common.edit')
        },
        {
          key: 'publish',
          label: t('workplace_giving.myCampaigns.actions.publishConfirmationModal.title')
        },
        {
          key: 'cancel',
          label: t('workplace_giving.myCampaigns.actions.cancelConfirmationModal.title')
        }
      ]
    }

    if (
      campaign.status === GivingCampaignStatusDto.PUBLISHED &&
      campaign.endDate &&
      isPast(new Date(campaign.endDate))
    ) {
      return [
        {
          key: 'view',
          label: t('workplace_giving.common.view')
        }
      ]
    }

    if (campaign.status === GivingCampaignStatusDto.PUBLISHED) {
      return [
        {
          key: 'view',
          label: t('workplace_giving.common.view')
        },
        {
          key: 'edit',
          label: t('workplace_giving.common.edit')
        },
        {
          key: 'unpublish',
          label: t('workplace_giving.myCampaigns.actions.unpublishConfirmationModal.title')
        },
        {
          key: 'cancel',
          label: t('workplace_giving.myCampaigns.actions.cancelConfirmationModal.title')
        }
      ]
    }

    if (campaign.status === GivingCampaignStatusDto.CANCELLED) {
      return [
        {
          key: 'view',
          label: t('workplace_giving.common.view')
        }
      ]
    }

    return []
  }, [campaign.status, campaign.endDate, t])

  const { view, edit, publish, unpublish, cancel } = useGivingCampaignActions({ campaign })

  const onSelect = (key: string) => {
    switch (key) {
      case 'view':
        view()
        break
      case 'edit':
        edit()
        break
      case 'publish':
        publish()
        break
      case 'unpublish':
        unpublish()
        break
      case 'cancel':
        cancel()
        break
      default:
    }
  }

  return <Menu title={t('workplace_giving.common.more')} onSelect={onSelect} sections={[{ items }]} />
}
